import { createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, withKeys as _withKeys, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
  class: "h-full overflow-auto"
};
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
import EditEntry from './EditEntry.vue';
import SnackbarComponent from '@/components/SnackbarComponent.vue';
import { useSnackbarStore } from '@/services/snackbarStore';
export default {
  __name: 'timeEntryModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    state: {
      type: Object,
      required: false,
      default: () => ({})
    },
    rootScope: {
      type: Object,
      required: false,
      default: () => ({})
    },
    alerts: {
      type: Object,
      required: false,
      default: () => ({})
    },
    snippetService: {
      type: Object,
      required: false,
      default: () => ({})
    },
    stateParams: {
      type: Object,
      default: () => ({})
    },
    params: {
      type: Object,
      default: () => ({})
    },
    entryEditService: {
      type: Object,
      required: false,
      default: () => ({})
    },
    tracking: {
      type: Object,
      required: false,
      default: () => ({})
    },
    clientMatterManager: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isTimeEntryPage: {
      type: Boolean,
      default: false
    },
    isFixedFeePage: {
      type: Boolean,
      default: false
    },
    hideDuplicateButton: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'addOrEditEntry', 'close', 'minimize'],
  setup(__props, {
    emit: __emit
  }) {
    const modalRoutes = ['timeplanner.new2', 'timeplanner.edit2', 'billing.invoice.matter.entry2', 'billing.invoice.builder.entry2', 'reports.generic.entry2'];
    const props = __props;
    const emit = __emit;
    const dialogOpen = ref(false);
    const modalRef = ref(null);
    const editEntryRef = ref(null);
    const snackbarStore = useSnackbarStore();

    // Determine if this modal is being used from the time entry page
    const isFromTimeEntryPage = computed(() => {
      const currentRouteName = props.state?.current?.name;
      // Only show expand/minimize buttons when on the actual time entries page
      return currentRouteName === 'timeentries' || currentRouteName === 'time';
    });

    // Computed property that is the single source of truth for opening the modal
    const shouldBeOpen = computed(() => {
      const currentRouteName = props.state?.current?.name;
      const isModalRoute = modalRoutes.includes(currentRouteName);
      return props.modelValue || isModalRoute;
    });
    watch(() => props.state?.current?.name, (newRouteName, oldRouteName) => {
      // Only handle route changes if we're NOT using v-model (i.e., modelValue is always false)
      // If modelValue is ever true, we're using v-model and should ignore route changes
      if (props.modelValue) {
        return;
      }

      // Force reactivity by directly checking route changes
      const shouldOpen = modalRoutes.includes(newRouteName);
      if (shouldOpen !== dialogOpen.value) {
        dialogOpen.value = shouldOpen;
        if (shouldOpen) {
          nextTick(() => {
            modalRef.value?.focus();
          });
        }
      }
    }, {
      immediate: true,
      flush: 'sync'
    });

    // Direct watch on modelValue to ensure immediate sync
    // Only respond to modelValue when it's explicitly being used (true) or changing
    watch(() => props.modelValue, (newVal, oldVal) => {
      // Check if we're on a modal route - if so, ignore modelValue changes
      const currentRoute = props.state?.current?.name;
      const isModalRoute = modalRoutes.includes(currentRoute);

      // If we're on a modal route, the route watcher handles opening/closing
      if (isModalRoute) {
        return;
      }
      if (!newVal && dialogOpen.value) {
        dialogOpen.value = false;
      } else if (newVal && !dialogOpen.value) {
        dialogOpen.value = true;
        nextTick(() => {
          modalRef.value?.focus();
        });
      }
    }, {
      immediate: true
    } // Keep immediate true for v-model mode
    );
    const handleKeydown = event => {
      if (event.key === 'Escape') {
        forceClose();
      }
    };

    // Separate handler for escape key
    const handleEscapeKey = () => {
      forceClose();
    };
    const forceClose = () => {
      // Try to call the EditEntry's close method first for unsaved changes check
      if (editEntryRef.value && editEntryRef.value.handleCloseClick) {
        editEntryRef.value.handleCloseClick();
      } else {
        // Fallback to direct close if ref is not available
        handleClose(false);
      }
    };
    const closeDialog = () => {
      dialogOpen.value = false;
      const currentRoute = props.state?.current?.name;
      if (modalRoutes.includes(currentRoute)) {
        props.state.go('^', {}, {
          reload: false
        });
      } else {
        // For v-model approach, emit update to close modal
        emit('update:modelValue', false);
      }
    };
    const handleClose = (isSuccess = false) => {
      emit('close', isSuccess);
      closeDialog();
    };
    const minimizeModal = () => {
      // Get current form state from EditEntry before minimizing
      let currentFormState = null;
      if (editEntryRef.value && editEntryRef.value.getFormState) {
        currentFormState = editEntryRef.value.getFormState();
      }
      emit('minimize', currentFormState);
    };
    const handleAddOrEditEntry = data => {
      emit('addOrEditEntry', data);
      if (data && (data.saved || data.deleted)) {
        // If opened from Fixed Fee page, close immediately
        if (props.isFixedFeePage) {
          closeDialog();
          return;
        }
        const currentRoute = props.state?.current?.name;

        // Check if we're in v-model mode (not route-based)
        const isVModelMode = props.modelValue || !modalRoutes.includes(currentRoute);
        if (isVModelMode) {
          // For v-model mode, always close via closeDialog
          closeDialog();
          return;
        }

        // For route-based mode, check if reload is needed
        const routesRequiringReload = ['billing.invoice.builder.entry2', 'billing.invoice.matter.entry2', 'reports.generic.entry2'];
        if (routesRequiringReload.includes(currentRoute)) {
          // For reload routes, navigate directly with reload - don't use closeDialog
          dialogOpen.value = false;

          // Special handling for reports routes - force complete page reload
          if (currentRoute === 'reports.generic.entry2') {
            // First navigate back to clear the entry route, then reload the page
            props.state.go('^', {}, {
              reload: false
            }).then(() => {
              // Use a small delay to ensure navigation is complete
              setTimeout(() => {
                window.location.reload();
              }, 100);
            });
          } else {
            // Use nextTick to ensure DOM updates before navigation
            nextTick(() => {
              props.state.go('^', {}, {
                reload: true
              });
            });
          }
        } else {
          // For non-reload routes, use the standard closeDialog method
          closeDialog();
        }
      }
    };
    const globalKeydownHandler = event => {
      if (event.key === 'Escape' && dialogOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        forceClose();
      }
    };
    onMounted(() => {
      document.addEventListener('keydown', globalKeydownHandler, true); // Use capture phase
    });
    onUnmounted(() => {
      document.removeEventListener('keydown', globalKeydownHandler, true);
    });
    return (_ctx, _cache) => {
      const _component_v_card_text = _resolveComponent("v-card-text");
      const _component_v_card = _resolveComponent("v-card");
      const _component_v_dialog = _resolveComponent("v-dialog");
      return _openBlock(), _createElementBlock(_Fragment, null, [_createVNode(SnackbarComponent), _createElementVNode("div", {
        onKeydown: [handleKeydown, _withKeys(handleEscapeKey, ["esc"])],
        ref_key: "modalRef",
        ref: modalRef,
        tabindex: "0",
        style: {
          "outline": "none"
        }
      }, [_createVNode(_component_v_dialog, {
        modelValue: dialogOpen.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => dialogOpen.value = $event),
        "max-width": "800px",
        "content-class": "time-entry-modal-dialog tw",
        scrollable: "",
        persistent: ""
      }, {
        default: _withCtx(() => [_createVNode(_component_v_card, {
          class: "h-screen max-h-screen d-flex flex-column"
        }, {
          default: _withCtx(() => [_createVNode(_component_v_card_text, {
            class: "flex-1-1 p-0"
          }, {
            default: _withCtx(() => [_createElementVNode("div", _hoisted_1, [_createVNode(EditEntry, {
              state: __props.state,
              rootScope: __props.rootScope,
              alerts: __props.alerts,
              snippetService: __props.snippetService,
              stateParams: __props.stateParams,
              params: __props.params,
              entryEditService: __props.entryEditService,
              tracking: __props.tracking,
              angularClientMatterManager: __props.clientMatterManager,
              isPanel: false,
              isModal: true,
              showMinimizeButton: true,
              isTimeEntryPage: isFromTimeEntryPage.value,
              hideDuplicateButton: __props.hideDuplicateButton,
              onClose: handleClose,
              onAddOrEditEntry: handleAddOrEditEntry,
              onMinimize: minimizeModal,
              ref_key: "editEntryRef",
              ref: editEntryRef
            }, null, 8, ["state", "rootScope", "alerts", "snippetService", "stateParams", "params", "entryEditService", "tracking", "angularClientMatterManager", "isTimeEntryPage", "hideDuplicateButton"])])]),
            _: 1
          })]),
          _: 1
        })]),
        _: 1
      }, 8, ["modelValue"])], 544)], 64);
    };
  }
};